/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import { withClient } from 'fierry/react';

import * as Hexagon from 'solis/type/point/hexagon';
import * as Constants from 'solis/react/board/geometry/constants';


export default withClient(class
{
  constructor ({ zoom })
  {
    this.zoom = zoom;

    this.pointsExternal = Hexagon.getPoints(this.getExternalWidth(), this.getExternalHeight());
    this.pointsInternal = Hexagon.getPoints(this.getInternalWidth(), this.getInternalHeight());

    this.pointsExternalInternal = Hexagon.getIntersectionPoints(this.pointsExternal, this.pointsInternal);
    this.pointsInternalExternal = Hexagon.getIntersectionPoints(this.pointsInternal, this.pointsExternal);
  }


  getExternalSize (ratio = 1)
  {
    return Hexagon.getSize(Constants.getExternalHeight(this.zoom)) * ratio;
  }


  getExternalWidth (ratio = 1)
  {
    return Hexagon.getWidth(this.getExternalSize(ratio)) * this.getStretchX();
  }


  getExternalHeight (ratio = 1)
  {
    return Hexagon.getHeight(this.getExternalSize(ratio)) * this.getStretchY();
  }


  getInternalSize (ratio = 1)
  {
    return Hexagon.getSize(Constants.getInternalHeight(this.zoom)) * ratio;
  }


  getInternalWidth (ratio = 1)
  {
    return Hexagon.getWidth(this.getInternalSize(ratio)) * this.getStretchX();
  }


  getInternalHeight (ratio = 1)
  {
    return Hexagon.getHeight(this.getInternalSize(ratio)) * this.getStretchY();
  }


  getExternalToInternalDistance ()
  {
    return this.getExternalSize() - this.getInternalSize();
  }


  getVerticalBorderSize ()
  {
    return this.getExternalSize();
  }


  getDiagonalBorderSize ()
  {
    const width = this.getExternalWidth();
    const height = this.getExternalHeight();

    return Math.sqrt(Math.pow(width / 2, 2) + Math.pow(height / 4, 2));
  }


  getEntitySize ()
  {
    return Constants.getEntitySize(this.zoom);
  }


  getExternalBorderWidth ()
  {
    return Constants.getExternalBorderWidth(this.zoom);
  }


  getEmphasizedBorderWidth ()
  {
    return Constants.getEmphasizedBorderWidth(this.zoom);
  }


  getGroupBorderWidth ()
  {
    return Constants.getGroupBorderWidth(this.zoom);
  }


  getEmphasizedGroupBorderWidth ()
  {
    return Constants.getEmphasizedGroupBorderWidth(this.zoom);
  }


  getZoneBorderWidth ()
  {
    return Constants.getZoneBorderWidth(this.zoom);
  }


  getDockBorderWidth ()
  {
    return Constants.getDockBorderWidth(this.zoom);
  }


  getJaggedBorderWidth ()
  {
    return Constants.getJaggedBorderWidth(this.zoom);
  }


  getJaggedStep ()
  {
    return Constants.getJaggedStep(this.zoom);
  }


  getJaggedAmplitude ()
  {
    return Constants.getJaggedAmplitude(this.zoom);
  }


  getStretchX ()
  {
    return 1;
  }


  getStretchY ()
  {
    return 1;
  }


  getExternalPoints ()
  {
    return this.pointsExternal;
  }


  getInternalPoints ()
  {
    return this.pointsInternal;
  }


  getPoints (zone)
  {
    let points = [];

    const neighbors = zone.getNeighbors();

    for (let i = 0; i < Hexagon.getPointCount(); ++i)
    {
      let prevInternal = zone.isExternalBorder(neighbors[Hexagon.getPointIndex(i - 1)]);
      let nextInternal = zone.isExternalBorder(neighbors[Hexagon.getPointIndex(i)]);

      if (prevInternal && nextInternal)
      {
        points.push(this.pointsInternal[i]);
      }

      else if (prevInternal && !nextInternal)
      {
        points.push(this.pointsInternalExternal[i]);
      }

      else if (!prevInternal && nextInternal)
      {
        points.push(this.pointsExternalInternal[i]);
      }

      else if (!prevInternal && !nextInternal)
      {
        points.push(this.pointsExternal[i]);
      }
    }

    return points;
  }


  getPointFromHex (hex)
  {
    const xSize = this.getExternalSize() * this.getStretchX();
    const ySize = this.getExternalSize() * this.getStretchY();

    return Hexagon.toPixel(hex, xSize, ySize);
  }


  getHexFromPoint (point)
  {
    const xSize = this.getExternalSize() * this.getStretchX();
    const ySize = this.getExternalSize() * this.getStretchY();

    return Hexagon.fromPixel(point, xSize, ySize);
  }


  isGroupVisible ()
  {
    return this.getExternalHeight() >= 36;
  }


  isEntityVisible ()
  {
    return this.getExternalHeight() >= 56;
  }


  isZoneVisible ()
  {
    return this.getExternalHeight() >= 72;
  }


});
